import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";

// Import components (make sure paths are correct)
import GroupPageShell from "../components/common/GroupPageShell";
import DivisionIntro from "../components/common/DivisionIntro";
import DivisionContact from "../components/common/DivisionContact";
import DivisionClients from "../components/common/DivisionClients";
import DivisionClientsStrip from "../components/common/DivisionClientsStrip";
import HamdanComplex from "../components/AL Baz Real Estate/HamdanComplex";
import AlBazCenter from "../components/AL Baz Real Estate/AlBazCenter";
import Maktabi from "../components/AL Baz Real Estate/Maktabi";
import { buildClientLogoImages } from "../utils/clientLogos";
import DivisionServices from "../components/common/DivisionServices";

const ALBazRealEstate: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [content, setContent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { t, t_api } = useLanguage();

  useEffect(() => {
    fetch('/api/homepage/content')
      .then(res => res.json())
      .then(data => {
        setContent(data.RealEstate || {});
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  // Get active tab from URL, default to "about"
  const activeTab = searchParams.get("tab") || "about";

  const menuItems = [
    { id: "about", label: t('common.about') },
    { id: "services", label: t('common.services') },
    { id: "albazcenter", label: t('common.albaz_center') },
    { id: "hamdancomplex", label: t('common.hamdan_complex') },
    { id: "clients", label: t('common.clients') },
    { id: "contact", label: t('common.contact') },
  ];

  if (loading) return <div className="min-h-screen flex items-center justify-center">
    <div className="w-16 h-16 border-4 border-red-50 border-t-red-600 rounded-full animate-spin mb-6" />
    <p className="text-gray-500 font-medium">{t('common.loading')}</p>
  </div>;

  const clientLogoImages = buildClientLogoImages(content);
  const clientLogoUrls = t_api(content?.clients_logo_urls, content?.clients_logo_urls_ar);

  return (
    <GroupPageShell
      heroContent={{
        title: t_api(content?.banner_title, content?.banner_title_ar),
        subtitle: t_api(content?.banner_subtitle, content?.banner_subtitle_ar),
        banners: [
          { image: content?.banner_1 || "/banner/albaz_1.jpg" },
          { image: content?.banner_2 || "/banner/albaz_2.jpg" }
        ]
      }}
      tabs={menuItems}
      activeTab={activeTab}
      onTabChange={(tabId) => setSearchParams({ tab: tabId })}
    >
      <div className="mt-8">
        {activeTab === "about" && (
          <>
            <DivisionIntro content={{
              kicker: "Hamdan Group - Oman",
              title: t_api(content?.about_title, content?.about_title_ar),
              desc: t_api(content?.about_desc, content?.about_desc_ar),
              image: content?.about_image || "/Hamdan-albaz-logo.jpg",
              imageFit: "contain",
              imageBadgeTitle: "Real Estate",
              imageLabel: t_api(content?.about_title, content?.about_title_ar) || "Al Baz Real Estate",
              noteTitle: "Portfolio",
              noteDesc: "Real estate services, property management, and commercial developments across Oman.",
              panelTitle: "INTRODUCTION",
              capabilitiesTitle: "Real Estate Capabilities",
              capabilitiesMeta: "Properties - Leasing - Management",
              brandLogo: "/Hamdan-albaz-logo.jpg",
              brandDesc: "A real estate division supporting residential, commercial, and managed property requirements.",
              brandTags: ["Real Estate", "Leasing", "Oman"],
              points: [1, 2, 3].map(i => ({
                title: t_api(content?.[`about_pt_${i}_title`], content?.[`about_pt_${i}_title_ar`]),
                desc: t_api(content?.[`about_pt_${i}_desc`], content?.[`about_pt_${i}_desc_ar`]),
                icon: ["MapPin", "Building2", "Star"][i - 1],
              })).filter(p => p.title)
            }} />
            <DivisionClientsStrip
              title={t_api(content?.clients_title, content?.clients_title_ar)}
              desc={t_api(content?.clients_desc, content?.clients_desc_ar)}
              clientsText={t_api(content?.clients_list, content?.clients_list_ar)}
              logoUrls={clientLogoUrls}
              logoImages={clientLogoImages}
            />
          </>
        )}
        {activeTab === "services" && (
          <DivisionServices content={content} />
        )}
        {activeTab === "albazcenter" && (
          <>
            <AlBazCenter content={{
              title: t_api(content?.center_title, content?.center_title_ar),
              desc: t_api(content?.center_desc, content?.center_desc_ar),
              objective: t_api(content?.center_objective, content?.center_objective_ar),
              image: content?.center_image,
              advantages: [1, 2, 3, 4, 5, 6].map(i => t_api(content?.[`center_adv_${i}`], content?.[`center_adv_${i}_ar`])).filter(Boolean),
              center_interior_1: content?.center_interior_1,
              center_interior_2: content?.center_interior_2,
              center_interior_3: content?.center_interior_3,
              center_interior_4: content?.center_interior_4,
            }} />
            <Maktabi content={{
              maktabi_intro: t_api(content?.maktabi_intro, content?.maktabi_intro_ar),
              maktabi_profile_desc: t_api(content?.maktabi_profile_desc, content?.maktabi_profile_desc_ar),
              maktabi_phil_1: t_api(content?.maktabi_phil_1, content?.maktabi_phil_1_ar),
              maktabi_phil_2: t_api(content?.maktabi_phil_2, content?.maktabi_phil_2_ar),
              maktabi_phil_3: t_api(content?.maktabi_phil_3, content?.maktabi_phil_3_ar),
              maktabi_phil_4: t_api(content?.maktabi_phil_4, content?.maktabi_phil_4_ar),
              maktabi_phil_5: t_api(content?.maktabi_phil_5, content?.maktabi_phil_5_ar),
              maktabi_phil_6: t_api(content?.maktabi_phil_6, content?.maktabi_phil_6_ar),
              maktabi_phil_7: t_api(content?.maktabi_phil_7, content?.maktabi_phil_7_ar),
              maktabi_image: content?.maktabi_image,
              maktabi_phone: content?.maktabi_phone,
              maktabi_website: content?.maktabi_website,
              maktabi_image_caption: t_api(content?.maktabi_image_caption, content?.maktabi_image_caption_ar)
            }} />
          </>
        )}
        {activeTab === "hamdancomplex" && <HamdanComplex content={{
          title: t_api(content?.complex_title, content?.complex_title_ar),
          desc1: t_api(content?.complex_desc_1, content?.complex_desc_1_ar),
          desc2: t_api(content?.complex_desc_2, content?.complex_desc_2_ar),
          image: content?.complex_image,
          services: [1, 2, 3, 4, 5, 6].map(i => t_api(content?.[`complex_service_${i}`], content?.[`complex_service_${i}_ar`])).filter(Boolean),
          ...Object.fromEntries(Object.entries(content || {}).filter(([k]) => k.startsWith('complex_layout_') || k.startsWith('complex_block_')))
        }} />}
        {activeTab === "clients" && <DivisionClients content={{
          title: t_api(content?.clients_title, content?.clients_title_ar),
          desc: t_api(content?.clients_desc, content?.clients_desc_ar),
          clientsText: t_api(content?.clients_list, content?.clients_list_ar),
          logoUrls: clientLogoUrls,
          logoImages: clientLogoImages,
          divisionLabel: "Hamdan Real Estate",
          footer: t_api(content?.clients_footer, content?.clients_footer_ar),
          footerDesc: t_api(content?.clients_footer_desc, content?.clients_footer_desc_ar),
        }} />}
        {activeTab === "contact" && <DivisionContact content={{
          title: t_api(content?.contact_form_title, content?.contact_form_title_ar),
          infoTitle: t_api(content?.contact_title, content?.contact_title_ar) || t('common.get_in_touch'),
          infoDesc: t_api(content?.contact_desc, content?.contact_desc_ar),
          contactInfo: [{
            location: "SALAH (Ghala) HQ",
            address: t_api(content?.contact_address, content?.contact_address_ar),
            phone: content?.contact_phone,
            fax: content?.contact_fax,
            email: content?.contact_email,
          }],
          supportEmail: content?.contact_email,
        }} />}
      </div>
    </GroupPageShell>
  );
};

export default ALBazRealEstate;
